import type { Experiment } from 'types';
import { useState } from 'react';
import { Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useExperimentPlan } from '@/state/experiments/useExperimentPlan';
import { isPreStart } from 'helpers/experiment-status';
import { formatRunLength, pct } from './plan-format';
import { PlanWizard } from './PlanWizard';

type PlanCardProps = { experiment: Experiment };

const SOURCE_LABEL = {
  yours: 'your figure',
  history: 'past tests here',
  site: 'site traffic',
  none: 'not set',
} as const;

type TrafficNoteParams = { source: keyof typeof SOURCE_LABEL; share: number | null; shareUnavailable: boolean };

/** "site traffic × 25%": the share is part of the assumption, so it is part of the label. */
const trafficNote = ({ source, share, shareUnavailable }: TrafficNoteParams) => {
  if (shareUnavailable) return `${SOURCE_LABEL[source]} (site traffic unavailable)`;
  return source === 'site' && share !== null && share < 100 ? `site traffic × ${share}%` : SOURCE_LABEL[source];
};

/**
 * How long this experiment needs to run, on the Brief tab under the PXL score.
 *
 * The same states as PXL. Until the wizard is completed there is only a prompt: no
 * estimate, and no request for one, because a number nobody chose reads as a plan.
 * Once saved, the card shows the plan with an Edit button.
 *
 * Editable while the experiment is a Draft. Once it runs, the card shows what was
 * planned, and the live projection is on the Statistics tab.
 */
export const PlanCard = ({ experiment }: PlanCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const manager = useExperimentPlan(experiment, { forWizard: isOpen });
  const isDraft = isPreStart(experiment.status);
  const { saved, current, assumptions } = manager;

  // Nothing to say about a test that ran without a plan.
  if (!isDraft && !saved) return null;

  if (!saved) {
    const canPlan = !manager.variantsLoading && manager.liveArms >= 2;
    return (
      <section className="bg-panel border-line rounded-panel border" data-tour="brief-plan">
        <div className="border-line border-b px-4 py-3">
          <h2 className="micro-label text-foreground">Run length</h2>
          <p className="supporting mt-1">
            Plan how long this experiment needs to run to detect the lift you care about.
          </p>
        </div>
        <div className="flex flex-col gap-2 p-4">
          <Button
            onClick={() => setIsOpen(true)}
            disabled={!canPlan}
            className="bg-cta hover:bg-ctahover h-8 w-full rounded-lg px-3 text-meta text-white"
          >
            Plan run length
          </Button>
          {!manager.variantsLoading && !canPlan && (
            <p className="supporting">
              {manager.arms < 2
                ? 'Add a variant to compare against the original first.'
                : 'Only one variant receives traffic. Split the weights to plan a comparison.'}
            </p>
          )}
        </div>
        <PlanWizard open={isOpen} onOpenChange={setIsOpen} manager={manager} />
      </section>
    );
  }

  const { plan, controlRate, dailySessions } = current;

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="brief-plan">
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="micro-label text-foreground">Run length</h2>
          <p className="supporting mt-1">
            {isDraft
              ? 'How long this needs to run to detect the lift you set.'
              : 'Planned before launch. Live progress is on the Statistics tab.'}
          </p>
        </div>
        {isDraft && (
          <Button
            variant="outline"
            size="sm"
            // Not until the figures arrive: the wizard picks its defaults from them when it
            // opens, and opening early seeded every step to "my own figure".
            disabled={manager.isLoading}
            onClick={() => setIsOpen(true)}
            className="border-line text-foreground hover:bg-hover h-8 shrink-0 rounded-lg border bg-transparent px-3 text-meta"
          >
            <Edit className="size-3.5" strokeWidth={1.5} />
            Edit
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 p-4">
        {manager.variantsLoading || manager.isLoading ? (
          <p className="supporting">Estimating…</p>
        ) : manager.isError ? (
          <p className="supporting">
            Could not load this website's traffic and conversion figures. Your own figures can still be entered.
          </p>
        ) : !isDraft && (manager.arms < 2 || manager.liveArms < 2 || !plan) ? (
          // After launch the arms can change (a deployed variant is no longer an arm), so a
          // live recomputation can be impossible. The assumptions themselves still stand.
          <p className="supporting">
            Planned for +{assumptions.liftPct}%, with{' '}
            {assumptions.dailySessions !== null
              ? `${assumptions.dailySessions.toLocaleString()} sessions a day`
              : assumptions.siteSharePct !== null
                ? `${assumptions.siteSharePct}% of the site's traffic`
                : "past tests' typical traffic"}
            {assumptions.ratePct !== null ? ` and a ${assumptions.ratePct}% conversion rate` : ''}.
          </p>
        ) : manager.arms < 2 || manager.liveArms < 2 ? (
          <p className="supporting">
            {manager.arms < 2
              ? 'Add a variant to compare against the original before planning.'
              : 'Only one variant receives traffic. Split the weights to plan a comparison.'}
          </p>
        ) : plan && controlRate !== null && dailySessions !== null ? (
          <>
            <div className="flex items-baseline justify-between gap-2">
              <span className="metric-value text-foreground">{formatRunLength(plan)}</span>
              <span className="micro-label">to detect +{assumptions.liftPct}% · 80% power</span>
            </div>
            <div className="border-line grid grid-cols-2 gap-2 border-t pt-4 sm:grid-cols-3">
              <Tile label="Per variant" value={plan.requiredPerArm.toLocaleString()} note="sessions" />
              <Tile
                label="Traffic"
                value={`${dailySessions.toLocaleString()}/day`}
                note={trafficNote({
                  source: current.trafficSource,
                  share: current.siteSharePct,
                  shareUnavailable: current.shareUnavailable,
                })}
              />
              <Tile label="Original rate" value={pct(controlRate, 2)} note={SOURCE_LABEL[current.rateSource]} />
            </div>
          </>
        ) : (
          <p className="supporting">
            No past experiments here to estimate traffic or conversion from. Enter your own figures to plan.
          </p>
        )}
      </div>

      {isDraft && <PlanWizard open={isOpen} onOpenChange={setIsOpen} manager={manager} />}
    </section>
  );
};

type TileProps = { label: string; value: string; note: string };

const Tile = ({ label, value, note }: TileProps) => (
  <div className="border-line bg-panel2 rounded-card flex min-w-0 flex-col gap-1 border p-3">
    <span className="micro-label truncate">{label}</span>
    <span className="num text-foreground truncate">{value}</span>
    <span className="text-dim truncate text-nano">{note}</span>
  </div>
);
